import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { mediaManagerClient } from '@/lib/axios';
import { MutationConfig, queryClient } from '@/lib/react-query';
import { useNotificationStore } from '@/stores/notifications';
import { emptyStringValues } from '@/utils/objects';
import { toAppWallClockString } from '@/utils/spain-time';

import { CNMC, CNMCStateEnum } from '../types';

import { cnmcQueryKeys } from './query-keys';

export type TPatchCNMCParams = {
  id: string;
  name?: string;
  description?: string;
  channel?: string;
  provider?: string;
  start_datetime?: Date;
  end_datetime?: Date;
  state?: CNMCStateEnum | null;
  recipients?: string | null;
};

export async function patchCNMC({ id, ...data }: TPatchCNMCParams): Promise<CNMC> {
  const formattedData = { ...data };

  if (formattedData.start_datetime) {
    formattedData.start_datetime.setSeconds(0, 0);
  }
  if (formattedData.end_datetime) {
    formattedData.end_datetime.setSeconds(0, 0);
  }

  return mediaManagerClient.patch(
    `/api/v1/cnmcs/${id}`,
    emptyStringValues({
      ...formattedData,
      start_datetime: formattedData.start_datetime
        ? toAppWallClockString(formattedData.start_datetime)
        : undefined,
      end_datetime: formattedData.end_datetime
        ? toAppWallClockString(formattedData.end_datetime)
        : undefined,
    })
  );
}

type MutationFn = typeof patchCNMC;

type TUsePatchCNMCParams = {
  config?: MutationConfig<MutationFn>;
};

export function usePatchCNMC({ config = {} }: TUsePatchCNMCParams = {}) {
  const { t } = useTranslation();
  const { addErrorNotification, addSuccessNotification } = useNotificationStore();

  return useMutation({
    ...config,
    mutationFn: patchCNMC,
    onError(error) {
      addErrorNotification(
        t('No se ha podido actualizar el ID del informe'),
        t('Ha habido un error en el proceso')
      );
      console.error(error);
    },
    onSuccess(data: CNMC, variables: TPatchCNMCParams, context: unknown) {
      addSuccessNotification(
        t('Se ha actualizado el ID del informe'),
        t('Los datos se han cargado correctamente')
      );
      queryClient.invalidateQueries({ queryKey: cnmcQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: cnmcQueryKeys.detail(variables.id) });
      config.onSuccess?.(data, variables, context);
    },
  });
}
